import React, { Fragment } from 'react'
import { bool, elementType } from 'prop-types'

const propTypes = {
    enable: bool,
    wrapper: elementType
}

const defaultProps = {
    enable: false
}

/**
 * A non-visual element to conditionally wrap its children. This element is meant to be used within the JSX
 * tree for better readability when conditional rendering is necessary. Instead of using embedded expressions as
 * inline JS code with logical operators in curly brackets, this element will not break the JSX syntax structure.
 * The children elements will be rendered (wrapped in a `React.Fragment`) with the given wrapper component, when the
 * given `enable` property is true. Otherwise, the children elements are rendered without the wrapping component.
 *
 * @example
 * <ConditionalWrapper enable={booleanFlag} wrapper={props => <Tooltip content="test">{props.children}</Tooltip>}>
 *     <div>Some simple jsx</div>
 * </Conditional>
 * <ConditionalWrapper enable={booleanFlag} wrapper={props => <Tooltip content="test">{props.children}</Tooltip>}>
 *     {() => <MyComplexComponent {...props} />}
 * </Conditional>
 *
 * @param {Object} props
 * @param {ReactNode|Function} [props.children] - The elements that conditionally shall be rendered with the wrapper.
 * @param {React.ElementType} props.wrapper - The wrapper component or function that conditionally is rendered with the children as property.
 * @param {boolean} [props.enable] - The condition when to render the children elements with the wrapper.
 * @returns {ReactNode} The wrapped children or `React.Fragment` wrapped children depending on `enable` condition.
 */
const ConditionalWrapper = props => {
    const { children, enable, wrapper: Wrapper } = props

    const WrappingComponent = !enable || !Wrapper ? Fragment : Wrapper
    return <WrappingComponent>{typeof children === 'function' ? children() : children}</WrappingComponent>
}

ConditionalWrapper.propTypes = propTypes
ConditionalWrapper.defaultProps = defaultProps

export { ConditionalWrapper }
